// src/pages/shops/EditShopPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Store,
  Upload,
  X,
  AlertCircle,
  MapPin,
  Phone,
  FileText,
  DollarSign,
  Package,
  Truck,
  Check,
  Banknote,
  Percent,
  Edit3,
  CreditCard,
  Calendar,
  Globe,
  ExternalLink,
  Wand2,
  Webhook,
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  getShop,
  updateShop,
  checkOrderPrefix,
  checkCatalogSlug,
  enableWoocommerceWebhook,
  disableWoocommerceWebhook,
  regenerateWoocommerceWebhookSecret,
  saveWoocommerceApiCredentials,
  testWoocommerceConnection,
} from '../../services/api/shops';
import { getShippingZones } from '../../services/api/shipping';
import { getBanks, getBankBranches } from '../../services/api/dropshipping';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../hooks/useAuth';
import { useAlert } from '../../contexts/AlertContext';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';
import { formatDateTime } from '../../utils/dateUtils';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import styles from './CreateShopPage.module.css'; // Reusing the same styles

const EditShopPage = () => {
  const navigate = useNavigate();
  const { shopId } = useParams();
  const { user } = useAuth();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  const { settings: messageCenterSettings } = useBusinessSettings('messageCenter');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    printableName: '',
    type: 'online',
    orderNumberPrefix: '',
    address: '',
    phone: '',
    logo: null,
    deliveryCharges: [],
    serviceCharge: '',
    // Reseller bank account (both roles can edit for dropshipper shops)
    resellerAccountName: '',
    resellerAccountNumber: '',
    resellerBankId: '',
    resellerBankBranchId: '',
    // Provider-only fields for dropshipper shops
    returnPenalty: '',
    paymentCycle: '',
    nextPaymentDate: '',
    // POS-specific fields
    allowCreditSales: false,
    cashierSessionRequired: false,
    // Catalog settings (provider-only, online/dropshipper shops)
    catalogEnabled: false,
    catalogSlug: '',
    messageCenterShiftEligible: false,
  });

  const [originalData, setOriginalData] = useState(null);
  const [shippingZones, setShippingZones] = useState([]);
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isCheckingPrefix, setIsCheckingPrefix] = useState(false);
  const [prefixStatus, setPrefixStatus] = useState(null);
  const [isCheckingCatalogSlug, setIsCheckingCatalogSlug] = useState(false);
  const [catalogSlugStatus, setCatalogSlugStatus] = useState(null); // null | 'available' | 'taken' | 'invalid'
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [showExtraCharges, setShowExtraCharges] = useState(false);

  // WooCommerce webhook integration
  const [woocommerceWebhook, setWoocommerceWebhook] = useState({
    enabled: false,
    secret: '',
    lastReceivedAt: null,
  });
  const [isWebhookToggling, setIsWebhookToggling] = useState(false);
  const [isRegeneratingSecret, setIsRegeneratingSecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  // WooCommerce REST API credentials (manual order sync). Key/secret are write-only:
  // the backend never returns them, only a masked key and a "saved" flag.
  const [woocommerceApi, setWoocommerceApi] = useState({
    storeUrl: '',
    consumerKey: '',
    consumerSecret: '',
    hasCredentials: false,
    maskedConsumerKey: null,
  });
  const [isSavingWoocommerceApi, setIsSavingWoocommerceApi] = useState(false);
  const [isTestingWoocommerceApi, setIsTestingWoocommerceApi] = useState(false);

  // Role state for dropshipper shops
  const [isProvider, setIsProvider] = useState(true);
  const [isReseller, setIsReseller] = useState(false);
  const [isDropshipperShop, setIsDropshipperShop] = useState(false);

  // Shop types
  const shopTypes = [
    { value: 'online', label: t('shops.online'), icon: Store },
    { value: 'pos', label: t('shops.pos'), icon: Package },
    { value: 'dropshipper', label: t('shops.dropshipper'), icon: Truck }
  ];

  // Load banks when entering a dropshipper shop
  useEffect(() => {
    if (isDropshipperShop) {
      getBanks().then(setBanks).catch(() => {});
    }
  }, [isDropshipperShop]);

  // Load branches when bank changes
  useEffect(() => {
    if (formData.resellerBankId) {
      getBankBranches(formData.resellerBankId)
        .then(setBranches)
        .catch(() => setBranches([]));
    } else {
      setBranches([]);
    }
  }, [formData.resellerBankId]);

  // Fetch shop data and shipping zones on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);

        const [shopResponse, zonesResponse] = await Promise.all([
          getShop(shopId),
          getShippingZones()
        ]);

        // Determine role: is the requesting business the provider (owner) or reseller?
        const userBusinessId = user?.businessId;
        const shopIsOwnedByUser = shopResponse.businessId === userBusinessId;
        const shopIsResellerForUser = shopResponse.resellerBusinessId === userBusinessId && !shopIsOwnedByUser;

        setIsProvider(shopIsOwnedByUser);
        setIsReseller(shopIsResellerForUser);
        setIsDropshipperShop(shopResponse.type === 'dropshipper');

        setOriginalData(shopResponse);

        setFormData({
          name: shopResponse.name || '',
          printableName: shopResponse.printableName || '',
          type: shopResponse.type || 'online',
          orderNumberPrefix: shopResponse.orderNumberPrefix || '',
          address: shopResponse.address || '',
          phone: shopResponse.phone || '',
          logo: null,
          deliveryCharges: shopResponse.DeliveryCharges || [],
          serviceCharge: shopResponse.serviceCharge || '',
          // Dropshipper reseller bank account fields
          resellerAccountName: shopResponse.resellerAccountName || '',
          resellerAccountNumber: shopResponse.resellerAccountNumber || '',
          resellerBankId: shopResponse.resellerBankId || '',
          resellerBankBranchId: shopResponse.resellerBankBranchId || '',
          // Provider-only dropshipper payment settings
          returnPenalty: shopResponse.returnPenalty != null ? String(shopResponse.returnPenalty) : '',
          paymentCycle: shopResponse.paymentCycle != null ? String(shopResponse.paymentCycle) : '',
          nextPaymentDate: shopResponse.nextPaymentDate || '',
          // POS-specific fields
          allowCreditSales: shopResponse.allowCreditSales || false,
          cashierSessionRequired: shopResponse.cashierSessionRequired || false,
          // Catalog settings
          catalogEnabled: shopResponse.catalogEnabled || false,
          catalogSlug: shopResponse.catalogSlug || '',
          messageCenterShiftEligible: shopResponse.messageCenterShiftEligible || false,
        });

        setWoocommerceWebhook({
          enabled: shopResponse.wooCommerceWebhookEnabled || false,
          secret: shopResponse.wooCommerceWebhookSecret || '',
          lastReceivedAt: shopResponse.wooCommerceLastWebhookReceivedAt || null,
        });

        if (shopResponse.woocommerceApi) {
          setWoocommerceApi(prev => ({
            ...prev,
            storeUrl: shopResponse.woocommerceApi.storeUrl || '',
            hasCredentials: shopResponse.woocommerceApi.hasCredentials,
            maskedConsumerKey: shopResponse.woocommerceApi.maskedConsumerKey,
          }));
        }

        if (shopResponse.logo) {
          setLogoPreview(getImageUrl(shopResponse.logo));
        }

        setShippingZones(zonesResponse);

        const hasExtraCharges = (shopResponse.DeliveryCharges || []).some(
          c => parseFloat(c.extraChargePerKg) > 0
        );
        setShowExtraCharges(hasExtraCharges);

        const existingZoneIds = shopResponse.DeliveryCharges?.map(charge => charge.shippingZoneId) || [];
        const missingZones = zonesResponse.filter(zone => !existingZoneIds.includes(zone.id));

        if (missingZones.length > 0) {
          const newCharges = missingZones.map(zone => ({
            shippingZoneId: zone.id,
            amount: '',
            extraChargePerKg: ''
          }));

          setFormData(prev => ({
            ...prev,
            deliveryCharges: [...(shopResponse.DeliveryCharges || []), ...newCharges]
          }));
        }

        // Load branches if bank is pre-selected
        if (shopResponse.resellerBankId) {
          getBankBranches(shopResponse.resellerBankId)
            .then(setBranches)
            .catch(() => {});
        }

      } catch (err) {
        console.error('Failed to fetch data:', err);
        if (err.response?.status === 404) {
          setErrors({ fetch: 'Shop not found. It may have been deleted.' });
        } else {
          setErrors({ fetch: 'Failed to load shop data. Please try again.' });
        }
      } finally {
        setIsLoadingData(false);
      }
    };

    if (shopId) {
      fetchData();
    }
  }, [shopId, user?.businessId]);

  // Debounced prefix validation (only if prefix changed and user is provider)
  useEffect(() => {
    if (isReseller) return; // resellers can't change prefix
    if (!formData.orderNumberPrefix ||
        formData.orderNumberPrefix.length < 2 ||
        formData.orderNumberPrefix === originalData?.orderNumberPrefix) {
      setPrefixStatus(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsCheckingPrefix(true);
        const response = await checkOrderPrefix(formData.orderNumberPrefix);
        setPrefixStatus(response.taken ? 'taken' : 'available');
      } catch (err) {
        console.error('Failed to check prefix:', err);
        setPrefixStatus(null);
      } finally {
        setIsCheckingPrefix(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.orderNumberPrefix, originalData?.orderNumberPrefix, isReseller]);

  // Debounced catalog slug availability check
  useEffect(() => {
    if (isReseller) return;
    const slug = formData.catalogSlug.trim().toLowerCase();
    if (!slug || slug === (originalData?.catalogSlug || '')) {
      setCatalogSlugStatus(null);
      return;
    }
    const timeoutId = setTimeout(async () => {
      try {
        setIsCheckingCatalogSlug(true);
        const result = await checkCatalogSlug(slug, shopId);
        if (!result.available && result.reason) {
          setCatalogSlugStatus('invalid');
        } else {
          setCatalogSlugStatus(result.available ? 'available' : 'taken');
        }
      } catch {
        setCatalogSlugStatus(null);
      } finally {
        setIsCheckingCatalogSlug(false);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.catalogSlug, originalData?.catalogSlug, shopId, isReseller]);

  const generateCatalogSlug = () => {
    const slug = formData.name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 80);
    setFormData(prev => ({ ...prev, catalogSlug: slug }));
    setCatalogSlugStatus(null);
    if (errors.catalogSlug) setErrors(prev => ({ ...prev, catalogSlug: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('shops.nameRequired');
    }

    if (!isReseller) {
      // Provider-only validations
      if (!formData.type) {
        newErrors.type = t('shops.typeRequired');
      }

      if (!formData.orderNumberPrefix.trim()) {
        newErrors.orderNumberPrefix = t('shops.prefixRequired');
      } else if (formData.orderNumberPrefix.length < 2) {
        newErrors.orderNumberPrefix = t('shops.prefixMinLength');
      } else if (prefixStatus === 'taken') {
        newErrors.orderNumberPrefix = t('shops.prefixTaken');
      }
    }

    if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = t('shops.invalidPhone');
    }

    // Delivery charges validation for online and dropshipper shops
    if ((formData.type === 'online' || formData.type === 'dropshipper') &&
        (!isReseller || isDropshipperShop)) {
      const hasEmptyCharges = formData.deliveryCharges.some(charge =>
        charge.amount === '' || charge.amount < 0
      );
      if (hasEmptyCharges) {
        newErrors.deliveryCharges = t('shops.deliveryChargesRequired');
      }
    }

    if (showExtraCharges && (formData.type === 'online' || formData.type === 'dropshipper')) {
      const hasInvalidExtraCharges = formData.deliveryCharges.some(charge =>
        charge.extraChargePerKg === '' || isNaN(charge.extraChargePerKg) || parseFloat(charge.extraChargePerKg) < 0
      );
      if (hasInvalidExtraCharges) {
        newErrors.extraCharges = t('shops.extraChargesRequired');
      }
    }

    if (!isReseller && formData.type === 'dropshipper') {
      if (!formData.serviceCharge || formData.serviceCharge === '') {
        newErrors.serviceCharge = t('shops.serviceChargeRequired');
      } else if (isNaN(formData.serviceCharge) || formData.serviceCharge < 0) {
        newErrors.serviceCharge = t('shops.invalidServiceCharge');
      }
    }

    if (formData.logo) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(formData.logo.type)) {
        newErrors.logo = t('shops.invalidLogoFormat');
      } else if (formData.logo.size > 5 * 1024 * 1024) {
        newErrors.logo = t('shops.logoTooLarge');
      }
    }

    // Catalog slug validation (provider-only, non-POS shops)
    if (!isReseller && formData.type !== 'pos') {
      if (formData.catalogEnabled && !formData.catalogSlug.trim()) {
        newErrors.catalogSlug = t('shops.catalogSlugRequired');
      } else if (
        formData.catalogSlug.trim() &&
        formData.catalogSlug.trim() !== (originalData?.catalogSlug || '') &&
        catalogSlugStatus === 'taken'
      ) {
        newErrors.catalogSlug = t('shops.catalogSlugTaken');
      } else if (
        formData.catalogSlug.trim() &&
        formData.catalogSlug.trim() !== (originalData?.catalogSlug || '') &&
        catalogSlugStatus === 'invalid'
      ) {
        newErrors.catalogSlug = t('shops.catalogSlugInvalid');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasChanges = () => {
    if (!originalData) return false;

    if (formData.name.trim() !== originalData.name ||
        formData.printableName.trim() !== (originalData.printableName || '') ||
        formData.address.trim() !== (originalData.address || '') ||
        formData.phone.trim() !== (originalData.phone || '')) {
      return true;
    }

    if (!isReseller) {
      if (formData.type !== originalData.type ||
          formData.orderNumberPrefix.trim() !== originalData.orderNumberPrefix) {
        return true;
      }
    }

    if (formData.logo || removeLogo) {
      return true;
    }

    if (!isReseller && formData.type === 'dropshipper') {
      const currentServiceCharge = parseFloat(formData.serviceCharge) || 0;
      const originalServiceCharge = parseFloat(originalData.serviceCharge) || 0;
      if (currentServiceCharge !== originalServiceCharge) return true;
    }

    if (!isReseller && formData.type === 'pos') {
      if (formData.allowCreditSales !== (originalData.allowCreditSales || false)) return true;
      if (formData.cashierSessionRequired !== (originalData.cashierSessionRequired || false)) return true;
    }

    if (!isReseller && formData.type !== 'pos') {
      if (formData.catalogEnabled !== (originalData.catalogEnabled || false)) return true;
      if ((formData.catalogSlug || '') !== (originalData.catalogSlug || '')) return true;
      if (formData.messageCenterShiftEligible !== (originalData.messageCenterShiftEligible || false)) return true;
    }

    if (isProvider && isDropshipperShop) {
      if ((formData.returnPenalty || '') !== (originalData.returnPenalty != null ? String(originalData.returnPenalty) : '')) return true;
      if ((formData.paymentCycle || '') !== (originalData.paymentCycle != null ? String(originalData.paymentCycle) : '')) return true;
      if ((formData.nextPaymentDate || '') !== (originalData.nextPaymentDate || '')) return true;
    }

    // Reseller bank account fields (both roles)
    if (isDropshipperShop) {
      if ((formData.resellerAccountName || '') !== (originalData.resellerAccountName || '')) return true;
      if ((formData.resellerAccountNumber || '') !== (originalData.resellerAccountNumber || '')) return true;
      if (String(formData.resellerBankId || '') !== String(originalData.resellerBankId || '')) return true;
      if (String(formData.resellerBankBranchId || '') !== String(originalData.resellerBankBranchId || '')) return true;
    }

    // Check delivery charges
    const currentCharges = formData.deliveryCharges
      .filter(charge => charge.amount !== '' && charge.amount >= 0)
      .sort((a, b) => a.shippingZoneId - b.shippingZoneId);

    const originalCharges = (originalData.DeliveryCharges || [])
      .sort((a, b) => a.shippingZoneId - b.shippingZoneId);

    if (currentCharges.length !== originalCharges.length) return true;

    for (let i = 0; i < currentCharges.length; i++) {
      const current = currentCharges[i];
      const orig = originalCharges[i];

      if (current.shippingZoneId !== orig.shippingZoneId ||
          parseFloat(current.amount) !== parseFloat(orig.amount)) {
        return true;
      }

      const currentExtra = showExtraCharges ? (parseFloat(current.extraChargePerKg) || 0) : 0;
      const originalExtra = parseFloat(orig.extraChargePerKg) || 0;
      if (currentExtra !== originalExtra) return true;
    }

    return false;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTypeChange = (type) => {
    if (isReseller) return; // resellers cannot change shop type
    setFormData(prev => ({
      ...prev,
      type,
      serviceCharge: type === 'dropshipper' ? prev.serviceCharge : ''
    }));
    if (errors.type) {
      setErrors(prev => ({ ...prev, type: '' }));
    }
  };

  const handleBankChange = (bankId) => {
    setFormData(prev => ({ ...prev, resellerBankId: bankId, resellerBankBranchId: '' }));
  };

  const handleDeliveryChargeChange = (zoneId, amount) => {
    setFormData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge =>
        charge.shippingZoneId === zoneId
          ? { ...charge, amount: amount === '' ? '' : parseFloat(amount) }
          : charge
      )
    }));
    if (errors.deliveryCharges) {
      setErrors(prev => ({ ...prev, deliveryCharges: '' }));
    }
  };

  const handleExtraChargeChange = (zoneId, value) => {
    setFormData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge =>
        charge.shippingZoneId === zoneId
          ? { ...charge, extraChargePerKg: value === '' ? '' : value }
          : charge
      )
    }));
    if (errors.extraCharges) {
      setErrors(prev => ({ ...prev, extraCharges: '' }));
    }
  };

  const handleBulkEditOpen = () => {
    setBulkEditValue('');
    setShowBulkEditModal(true);
  };

  const handleBulkEditApply = () => {
    if (bulkEditValue === '' || isNaN(bulkEditValue) || parseFloat(bulkEditValue) < 0) {
      error(t('shops.invalidBulkEditValue'));
      return;
    }
    setFormData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge => ({
        ...charge,
        amount: parseFloat(bulkEditValue)
      }))
    }));
    if (errors.deliveryCharges) {
      setErrors(prev => ({ ...prev, deliveryCharges: '' }));
    }
    setShowBulkEditModal(false);
    success(t('shops.bulkEditApplied'));
  };

  const handleSetFreeDeliveryAll = () => {
    setFormData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge => ({ ...charge, amount: 0 }))
    }));
    if (errors.deliveryCharges) {
      setErrors(prev => ({ ...prev, deliveryCharges: '' }));
    }
    success(t('shops.freeDeliveryApplied'));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }));
      setRemoveLogo(false);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
    if (errors.logo) {
      setErrors(prev => ({ ...prev, logo: '' }));
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
    setLogoPreview(null);
    setRemoveLogo(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getWoocommerceWebhookUrls = () => {
    const baseUrl = process.env.REACT_APP_IMAGE_BASE_URL ||
      process.env.REACT_APP_API_URL?.replace('/api', '') ||
      'http://localhost:5000';
    return {
      orderCreatedUrl: `${baseUrl}/api/webhooks/woocommerce/${shopId}/order-created`,
      orderStatusUpdatedUrl: `${baseUrl}/api/webhooks/woocommerce/${shopId}/order-status-updated`,
    };
  };

  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      success(t('shops.woocommerceCopied'), { duration: 2000 });
    } catch (err) {
      error(t('shops.woocommerceCopyFailed'));
    }
  };

  const handleToggleWoocommerceWebhook = async (enabled) => {
    setIsWebhookToggling(true);
    try {
      if (enabled) {
        const result = await enableWoocommerceWebhook(shopId);
        setWoocommerceWebhook(prev => ({ ...prev, enabled: true, secret: result.webhookSecret }));
        setShowWebhookSecret(true);
        success(t('shops.woocommerceWebhookEnabledSuccess'));
      } else {
        await disableWoocommerceWebhook(shopId);
        setWoocommerceWebhook(prev => ({ ...prev, enabled: false }));
        success(t('shops.woocommerceWebhookDisabledSuccess'));
      }
    } catch (err) {
      error(err.response?.data?.message || t('shops.woocommerceWebhookToggleFailed'));
    } finally {
      setIsWebhookToggling(false);
    }
  };

  const handleRegenerateWebhookSecret = async () => {
    const confirmed = await confirm(t('shops.woocommerceRegenerateSecretConfirm'), {
      title: t('shops.woocommerceRegenerateSecretConfirmTitle'),
      confirmText: t('shops.woocommerceRegenerateSecret'),
      cancelText: t('common.cancel'),
    });
    if (!confirmed) return;

    setIsRegeneratingSecret(true);
    try {
      const result = await regenerateWoocommerceWebhookSecret(shopId);
      setWoocommerceWebhook(prev => ({ ...prev, secret: result.webhookSecret }));
      setShowWebhookSecret(true);
      success(t('shops.woocommerceRegenerateSecretSuccess'));
    } catch (err) {
      error(err.response?.data?.message || t('shops.woocommerceRegenerateSecretFailed'));
    } finally {
      setIsRegeneratingSecret(false);
    }
  };

  const handleSaveWoocommerceApi = async () => {
    setIsSavingWoocommerceApi(true);
    try {
      const result = await saveWoocommerceApiCredentials(shopId, {
        storeUrl: woocommerceApi.storeUrl.trim(),
        consumerKey: woocommerceApi.consumerKey.trim(),
        consumerSecret: woocommerceApi.consumerSecret.trim(),
      });
      setWoocommerceApi(prev => ({
        ...prev,
        storeUrl: result.storeUrl || prev.storeUrl,
        consumerKey: '',
        consumerSecret: '',
        hasCredentials: result.hasCredentials,
        maskedConsumerKey: result.maskedConsumerKey,
      }));
      success(t('shops.woocommerceApiSaveSuccess'));
    } catch (err) {
      error(err.response?.data?.message || t('shops.woocommerceApiSaveFailed'));
    } finally {
      setIsSavingWoocommerceApi(false);
    }
  };

  const handleTestWoocommerceApi = async () => {
    setIsTestingWoocommerceApi(true);
    try {
      await testWoocommerceConnection(shopId);
      success(t('shops.woocommerceTestSuccess'));
    } catch (err) {
      error(err.response?.data?.message || t('shops.woocommerceTestFailed'));
    } finally {
      setIsTestingWoocommerceApi(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!hasChanges()) {
      error(t('shops.noChangesToSave'), {
        title: t('shops.noChanges'),
        duration: 3000
      });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      submitData.append('printableName', formData.printableName.trim());

      if (!isReseller) {
        submitData.append('type', formData.type);
        submitData.append('orderNumberPrefix', formData.orderNumberPrefix.trim());
      }

      if (formData.address.trim()) {
        submitData.append('address', formData.address.trim());
      }

      if (formData.phone.trim()) {
        submitData.append('phone', formData.phone.trim());
      }

      if (formData.logo) {
        submitData.append('logo', formData.logo);
      } else if (removeLogo) {
        submitData.append('removeLogo', 'true');
      }

      // Delivery charges (both roles can submit for dropshipper shops)
      const shopType = formData.type || originalData?.type;
      if (shopType === 'online' || shopType === 'dropshipper') {
        const validDeliveryCharges = formData.deliveryCharges
          .filter(charge => charge.amount !== '' && charge.amount >= 0)
          .map(charge => ({
            shippingZoneId: charge.shippingZoneId,
            amount: parseFloat(charge.amount),
            extraChargePerKg: showExtraCharges ? (parseFloat(charge.extraChargePerKg) || 0) : 0,
          }));
        submitData.append('deliveryCharges', JSON.stringify(validDeliveryCharges));
      }

      // Provider-only fields
      if (!isReseller) {
        if (formData.type === 'dropshipper' && formData.serviceCharge) {
          submitData.append('serviceCharge', parseFloat(formData.serviceCharge));
        }

        if (formData.type === 'pos') {
          submitData.append('allowCreditSales', formData.allowCreditSales);
          submitData.append('cashierSessionRequired', formData.cashierSessionRequired);
        }

        // Catalog settings — online and dropshipper shops only
        if (formData.type !== 'pos') {
          submitData.append('catalogEnabled', formData.catalogEnabled);
          submitData.append('catalogSlug', formData.catalogSlug.trim().toLowerCase());
          submitData.append('messageCenterShiftEligible', formData.messageCenterShiftEligible);
        }

        if (isDropshipperShop) {
          if (formData.returnPenalty !== '') {
            submitData.append('returnPenalty', parseFloat(formData.returnPenalty) || 0);
          }
          if (formData.paymentCycle !== '') {
            submitData.append('paymentCycle', parseInt(formData.paymentCycle) || 7);
          }
          if (formData.nextPaymentDate) {
            submitData.append('nextPaymentDate', formData.nextPaymentDate);
          }
        }
      }

      // Reseller bank account fields (both roles can submit for dropshipper shops)
      if (isDropshipperShop) {
        submitData.append('resellerAccountName', formData.resellerAccountName || '');
        submitData.append('resellerAccountNumber', formData.resellerAccountNumber || '');
        submitData.append('resellerBankId', formData.resellerBankId || '');
        submitData.append('resellerBankBranchId', formData.resellerBankBranchId || '');
      }

      const response = await updateShop(shopId, submitData);
      success(t('shops.shopUpdatedSuccess', { shopName: response.shop.name }), {
        title: t('common.success'),
        duration: 4000
      });
      navigate('/shops');
    } catch (err) {
      if (err.response?.data?.message) {
        setErrors({ submit: err.response.data.message });
      } else {
        setErrors({ submit: t('shops.failedToUpdate') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (hasChanges()) {
      const shouldDiscard = await confirm(t('shops.confirmDiscardChanges'), {
        title: t('shops.unsavedChanges'),
        confirmText: t('shops.discardChanges'),
        cancelText: t('common.cancel')
      });

      if (shouldDiscard) {
        navigate('/shops');
      }
    } else {
      navigate('/shops');
    }
  };

  if (isLoadingData) {
    return (
      <div className={styles.createShopPage}>
        <PageHeader
          title={t('shops.editShop')}
          description={t('shops.updateShopDetails')}
          icon={Store}
          actions={
            <button
              onClick={() => navigate('/shops')}
              className={styles.backButton}
            >
              <ArrowLeft size={20} />
              {t('shops.backToShops')}
            </button>
          }
        />
        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('shops.loadingData')}</h3>
            <p>{t('shops.loadingDataDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (errors.fetch) {
    return (
      <div className={styles.createShopPage}>
        <PageHeader
          title={t('shops.editShop')}
          description={t('shops.updateShopDetails')}
          icon={Store}
          actions={
            <button
              onClick={() => navigate('/shops')}
              className={styles.backButton}
            >
              <ArrowLeft size={20} />
              {t('shops.backToShops')}
            </button>
          }
        />
        <div className={styles.pageContent}>
          <div className={styles.errorState}>
            <AlertCircle className={styles.errorIcon} size={48} />
            <h3>{t('errors.somethingWentWrong')}</h3>
            <p>{errors.fetch}</p>
            <button onClick={() => navigate('/shops')} className={styles.retryButton}>
              <ArrowLeft size={16} />
              {t('shops.backToShops')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createShopPage}>
      <PageHeader
        title={t('shops.editShop')}
        description={t('shops.updateShopDetails')}
        icon={Store}
        actions={
          <button
            onClick={handleCancel}
            className={styles.backButton}
          >
            <ArrowLeft size={20} />
            {t('shops.backToShops')}
          </button>
        }
      />

      <div className={styles.pageContent}>
        {isReseller && isDropshipperShop && (
          <div style={{
            padding: '12px 16px',
            background: 'var(--color-info-bg, #eff6ff)',
            border: '1px solid var(--color-info-border, #bfdbfe)',
            borderRadius: '8px',
            color: 'var(--color-info-text, #1d4ed8)',
            marginBottom: '16px',
            fontSize: '14px',
          }}>
            You are editing a dropshipper shop owned by your provider. Some fields are managed by the provider and cannot be changed here.
          </div>
        )}

        {errors.submit && (
          <div className={styles.errorBanner}>
            <AlertCircle size={20} />
            <div>
              <p>{errors.submit}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.shopForm}>
          {/* Basic Information */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <h2>{t('shops.basicInformation')}</h2>
              <p>{t('shops.basicInformationDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="name">{t('shops.shopName')} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('shops.shopNamePlaceholder')}
                    className={errors.name ? 'error' : ''}
                    required
                  />
                  {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="printableName">{t('shops.printableName')}</label>
                  <input
                    type="text"
                    id="printableName"
                    name="printableName"
                    value={formData.printableName}
                    onChange={handleInputChange}
                    placeholder={t('shops.printableNamePlaceholder')}
                  />
                  <span className={styles.formHint}>{t('shops.printableNameHint')}</span>
                </div>
              </div>

              <div className={styles.formRow}>
                {/* Order prefix — disabled for resellers */}
                <div className={styles.formGroup}>
                  <label htmlFor="orderNumberPrefix">
                    {t('shops.orderPrefix')} *
                    {isReseller && (
                      <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 'normal', marginLeft: '6px', fontSize: '12px' }}>
                        (managed by provider)
                      </span>
                    )}
                  </label>
                  <div className={styles.prefixInputWrapper}>
                    <input
                      type="text"
                      id="orderNumberPrefix"
                      name="orderNumberPrefix"
                      value={formData.orderNumberPrefix}
                      onChange={handleInputChange}
                      placeholder="ABC"
                      className={`${styles.prefixInput} ${errors.orderNumberPrefix ? 'error' : ''} ${prefixStatus === 'available' ? 'success' : ''}`}
                      maxLength="5"
                      style={{ textTransform: 'uppercase' }}
                      onInput={(e) => e.target.value = e.target.value.toUpperCase()}
                      readOnly={isReseller}
                      disabled={isReseller}
                      required={!isReseller}
                    />
                    {!isReseller && (
                      <div className={styles.prefixStatus}>
                        {isCheckingPrefix && <div className={styles.checkingSpinner}></div>}
                        {!isCheckingPrefix && prefixStatus === 'available' && (
                          <Check size={16} className={`${styles.statusIcon} ${styles.success}`} />
                        )}
                        {!isCheckingPrefix && prefixStatus === 'taken' && (
                          <X size={16} className={`${styles.statusIcon} ${styles.error}`} />
                        )}
                      </div>
                    )}
                  </div>
                  {errors.orderNumberPrefix && <span className={styles.errorText}>{errors.orderNumberPrefix}</span>}
                  {!isReseller && !errors.orderNumberPrefix && prefixStatus === 'available' && (
                    <span className={styles.successText}>{t('shops.prefixAvailable')}</span>
                  )}
                  <span className={styles.formHint}>{t('shops.prefixHint')}</span>
                </div>
              </div>

              {/* Shop type — disabled for resellers */}
              <div className={styles.formGroup}>
                <label>
                  {t('shops.shopType')} *
                  {isReseller && (
                    <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 'normal', marginLeft: '6px', fontSize: '12px' }}>
                      (managed by provider)
                    </span>
                  )}
                </label>
                <div className={styles.shopTypeGrid}>
                  {shopTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        className={`${styles.shopTypeCard} ${formData.type === type.value ? styles.selected : ''}`}
                        onClick={() => handleTypeChange(type.value)}
                        disabled={isReseller}
                        style={isReseller ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                      >
                        <Icon size={24} className={styles.typeIcon} />
                        <span className={styles.typeLabel}>{type.label}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.type && <span className={styles.errorText}>{errors.type}</span>}
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="address">{t('shops.address')}</label>
                  <div className={styles.inputWithIcon}>
                    <MapPin size={18} className={styles.inputIcon} />
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder={t('shops.addressPlaceholder')}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone">{t('shops.phone')}</label>
                  <div className={styles.inputWithIcon}>
                    <Phone size={18} className={styles.inputIcon} />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('shops.phonePlaceholder')}
                      className={errors.phone ? 'error' : ''}
                    />
                  </div>
                  {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Logo Upload */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <h2>{t('shops.shopLogo')}</h2>
              <p>{t('shops.logoDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.logoUploadSection}>
                {!logoPreview ? (
                  <div className={styles.logoUploadArea} onClick={() => fileInputRef.current?.click()}>
                    <Upload size={32} className={styles.uploadIcon} />
                    <p className={styles.uploadText}>{t('shops.uploadLogo')}</p>
                    <p className={styles.uploadHint}>{t('shops.logoFormats')}</p>
                  </div>
                ) : (
                  <div className={styles.logoPreview}>
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className={styles.previewImage}
                      onError={(e) => handleImageError(e)}
                    />
                    <button
                      type="button"
                      className={styles.removeLogoButton}
                      onClick={handleRemoveLogo}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={handleLogoChange}
                  style={{ display: 'none' }}
                />

                {errors.logo && <span className={styles.errorText}>{errors.logo}</span>}
              </div>
            </div>
          </div>

          {/* Delivery Charges — visible for both roles on dropshipper/online shops */}
          {(formData.type === 'online' || formData.type === 'dropshipper') && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <div className={styles.cardHeaderWithActions}>
                  <div>
                    <h2>{t('shops.deliveryCharges')}</h2>
                    <p>{t('shops.deliveryChargesDesc')}</p>
                  </div>
                  <div className={styles.bulkActions}>
                    <button
                      type="button"
                      onClick={handleBulkEditOpen}
                      className={styles.bulkEditButton}
                    >
                      <Edit3 size={16} />
                      {t('shops.bulkEdit')}
                    </button>
                    <button
                      type="button"
                      onClick={handleSetFreeDeliveryAll}
                      className={styles.freeDeliveryButton}
                    >
                      <Package size={16} />
                      {t('shops.setFreeDeliveryAll')}
                    </button>
                  </div>
                </div>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.deliveryChargesGrid}>
                  {shippingZones.map((zone) => {
                    const charge = formData.deliveryCharges.find(c => c.shippingZoneId === zone.id);
                    return (
                      <div key={zone.id} className={styles.chargeInputGroup}>
                        <label htmlFor={`charge-${zone.id}`}>
                          {zone.name} <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.inputWithIcon}>
                          <Banknote size={18} className={styles.inputIcon} />
                          <input
                            type="number"
                            id={`charge-${zone.id}`}
                            value={charge?.amount === 0 ? 0 : (charge?.amount || '')}
                            onChange={(e) => handleDeliveryChargeChange(zone.id, e.target.value)}
                            placeholder="150.00"
                            min="0"
                            step="0.01"
                            className={`${styles.deliveryInput} ${errors.deliveryCharges ? 'error' : ''}`}
                            required
                          />
                          <span className={styles.currencyLabel}>{user?.business?.currency || null}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {errors.deliveryCharges && <span className={styles.errorText}>{errors.deliveryCharges}</span>}

                <div className={styles.extraChargesToggle}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={showExtraCharges}
                      onChange={(e) => {
                        setShowExtraCharges(e.target.checked);
                        if (errors.extraCharges) setErrors(prev => ({ ...prev, extraCharges: '' }));
                      }}
                      className={styles.checkbox}
                    />
                    <span>{t('shops.setupExtraCharges')}</span>
                  </label>
                </div>

                {showExtraCharges && (
                  <div className={styles.extraChargesSection}>
                    <p className={styles.extraChargesDesc}>{t('shops.extraChargesDesc')}</p>
                    <div className={styles.deliveryChargesGrid}>
                      {shippingZones.map((zone) => {
                        const charge = formData.deliveryCharges.find(c => c.shippingZoneId === zone.id);
                        return (
                          <div key={zone.id} className={styles.chargeInputGroup}>
                            <label htmlFor={`extra-${zone.id}`}>
                              {zone.name} <span className={styles.required}>*</span>
                            </label>
                            <div className={styles.inputWithIcon}>
                              <Banknote size={18} className={styles.inputIcon} />
                              <input
                                type="number"
                                id={`extra-${zone.id}`}
                                value={charge?.extraChargePerKg === 0 ? 0 : (charge?.extraChargePerKg || '')}
                                onChange={(e) => handleExtraChargeChange(zone.id, e.target.value)}
                                placeholder={t('shops.extraChargePerKgPlaceholder')}
                                min="0"
                                step="0.01"
                                className={`${styles.deliveryInput} ${errors.extraCharges ? 'error' : ''}`}
                              />
                              <span className={styles.currencyLabel}>{user?.business?.currency || null}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {errors.extraCharges && <span className={styles.errorText}>{errors.extraCharges}</span>}
                  </div>
                )}

                <div className={styles.deliveryHint}>
                  <p>{t('shops.deliveryChargesHint')}</p>
                </div>
              </div>
            </div>
          )}

          {/* POS Settings — provider only */}
          {!isReseller && formData.type === 'pos' && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('shops.posSettings')}</h2>
                <p>{t('shops.posSettingsDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.allowCreditSales}
                      onChange={(e) => setFormData(prev => ({ ...prev, allowCreditSales: e.target.checked }))}
                      className={styles.checkbox}
                    />
                    <span>{t('shops.allowCreditSales')}</span>
                  </label>
                  <span className={styles.formHint}>{t('shops.allowCreditSalesHint')}</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.cashierSessionRequired}
                      onChange={(e) => setFormData(prev => ({ ...prev, cashierSessionRequired: e.target.checked }))}
                      className={styles.checkbox}
                    />
                    <span>{t('shops.cashierSessionRequired')}</span>
                  </label>
                  <span className={styles.formHint}>{t('shops.cashierSessionRequiredHint')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Service Charge — provider only, not visible to resellers */}
          {!isReseller && formData.type === 'dropshipper' && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('shops.serviceCharge')}</h2>
                <p>{t('shops.serviceChargeDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formGroup}>
                  <label htmlFor="serviceCharge">{t('shops.serviceChargeAmount')} *</label>
                  <div className={styles.inputWithIcon}>
                    <Banknote size={18} className={styles.inputIcon} />
                    <input
                      type="number"
                      id="serviceCharge"
                      name="serviceCharge"
                      value={formData.serviceCharge}
                      onChange={handleInputChange}
                      placeholder="50.00"
                      min="0"
                      step="0.01"
                      className={errors.serviceCharge ? 'error' : ''}
                      required
                    />
                    <span className={styles.currencyLabel}>{user?.business?.currency || null}</span>
                  </div>
                  {errors.serviceCharge && <span className={styles.errorText}>{errors.serviceCharge}</span>}
                  <span className={styles.formHint}>{t('shops.serviceChargeHint')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Dropshipping Payment Settings — provider only */}
          {isProvider && isDropshipperShop && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>Dropshipping Payment Settings</h2>
                <p>Configure payment cycle, return penalty, and payment schedule for this reseller shop.</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="returnPenalty">Return Penalty</label>
                    <div className={styles.inputWithIcon}>
                      <Banknote size={18} className={styles.inputIcon} />
                      <input
                        type="number"
                        id="returnPenalty"
                        name="returnPenalty"
                        value={formData.returnPenalty}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                      <span className={styles.currencyLabel}>{user?.business?.currency || null}</span>
                    </div>
                    <span className={styles.formHint}>Amount charged to reseller per returned order.</span>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="paymentCycle">Payment Cycle (days)</label>
                    <div className={styles.inputWithIcon}>
                      <Calendar size={18} className={styles.inputIcon} />
                      <input
                        type="number"
                        id="paymentCycle"
                        name="paymentCycle"
                        value={formData.paymentCycle}
                        onChange={handleInputChange}
                        placeholder="7"
                        min="1"
                        step="1"
                      />
                    </div>
                    <span className={styles.formHint}>How often the reseller is settled (e.g. 7 = weekly).</span>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="nextPaymentDate">Next Payment Date</label>
                  <div className={styles.inputWithIcon}>
                    <Calendar size={18} className={styles.inputIcon} />
                    <input
                      type="date"
                      id="nextPaymentDate"
                      name="nextPaymentDate"
                      value={formData.nextPaymentDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reseller Bank Account — shown for dropshipper shops (both roles) */}
          {isDropshipperShop && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>Reseller Bank Account</h2>
                <p>Bank account details for settling payments to the reseller.</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="resellerAccountName">Account Holder Name</label>
                    <div className={styles.inputWithIcon}>
                      <CreditCard size={18} className={styles.inputIcon} />
                      <input
                        type="text"
                        id="resellerAccountName"
                        name="resellerAccountName"
                        value={formData.resellerAccountName}
                        onChange={handleInputChange}
                        placeholder="e.g. John Silva"
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="resellerAccountNumber">Account Number</label>
                    <div className={styles.inputWithIcon}>
                      <CreditCard size={18} className={styles.inputIcon} />
                      <input
                        type="text"
                        id="resellerAccountNumber"
                        name="resellerAccountNumber"
                        value={formData.resellerAccountNumber}
                        onChange={handleInputChange}
                        placeholder="e.g. 0123456789"
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="resellerBankId">Bank</label>
                    <select
                      id="resellerBankId"
                      name="resellerBankId"
                      value={formData.resellerBankId}
                      onChange={(e) => handleBankChange(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px',
                        background: 'var(--color-bg-primary)',
                        color: 'var(--color-text-primary)',
                        fontSize: '14px',
                      }}
                    >
                      <option value="">Select a bank</option>
                      {banks.map(bank => (
                        <option key={bank.id} value={bank.id}>{bank.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="resellerBankBranchId">Branch</label>
                    <select
                      id="resellerBankBranchId"
                      name="resellerBankBranchId"
                      value={formData.resellerBankBranchId}
                      onChange={handleInputChange}
                      disabled={!formData.resellerBankId || branches.length === 0}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px',
                        background: 'var(--color-bg-primary)',
                        color: 'var(--color-text-primary)',
                        fontSize: '14px',
                        opacity: (!formData.resellerBankId || branches.length === 0) ? 0.6 : 1,
                      }}
                    >
                      <option value="">Select a branch</option>
                      {branches.map(branch => (
                        <option key={branch.id} value={branch.id}>{branch.branchName}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Catalog Settings — provider-only, online and dropshipper shops */}
          {isProvider && formData.type !== 'pos' && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('shops.catalogSettings')}</h2>
                <p>{t('shops.catalogSettingsDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                {/* Enable toggle */}
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.catalogEnabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, catalogEnabled: e.target.checked }))}
                      className={styles.checkbox}
                    />
                    <span>{t('shops.catalogEnabled')}</span>
                  </label>
                  <span className={styles.formHint}>{t('shops.catalogEnabledHint')}</span>
                </div>

                {/* Slug input — always visible so the business can set it before enabling */}
                <div className={styles.formGroup}>
                  <label htmlFor="catalogSlug">{t('shops.catalogSlug')}</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <div className={styles.prefixInputWrapper} style={{ flex: 1 }}>
                      <div className={styles.inputWithIcon}>
                        <Globe size={18} className={styles.inputIcon} />
                        <input
                          type="text"
                          id="catalogSlug"
                          name="catalogSlug"
                          value={formData.catalogSlug}
                          onChange={(e) => {
                            const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            setFormData(prev => ({ ...prev, catalogSlug: v }));
                            setCatalogSlugStatus(null);
                            if (errors.catalogSlug) setErrors(prev => ({ ...prev, catalogSlug: '' }));
                          }}
                          placeholder={t('shops.catalogSlugPlaceholder')}
                          className={errors.catalogSlug ? 'error' : ''}
                          maxLength={80}
                        />
                      </div>
                      <div className={styles.prefixStatus}>
                        {isCheckingCatalogSlug && <div className={styles.checkingSpinner}></div>}
                        {!isCheckingCatalogSlug && catalogSlugStatus === 'available' && (
                          <Check size={16} className={`${styles.statusIcon} ${styles.success}`} />
                        )}
                        {!isCheckingCatalogSlug && (catalogSlugStatus === 'taken' || catalogSlugStatus === 'invalid') && (
                          <X size={16} className={`${styles.statusIcon} ${styles.error}`} />
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={generateCatalogSlug}
                      title={t('shops.generateSlug')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '9px 12px',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px',
                        background: 'var(--color-bg-secondary)',
                        color: 'var(--color-text-secondary)',
                        fontSize: '13px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      <Wand2 size={14} />
                      {t('shops.generateSlug')}
                    </button>
                  </div>

                  {errors.catalogSlug && <span className={styles.errorText}>{errors.catalogSlug}</span>}
                  {!errors.catalogSlug && !isCheckingCatalogSlug && catalogSlugStatus === 'available' && (
                    <span className={styles.successText}>{t('shops.catalogSlugAvailable')}</span>
                  )}
                  {!errors.catalogSlug && !isCheckingCatalogSlug && catalogSlugStatus === 'taken' && (
                    <span className={styles.errorText}>{t('shops.catalogSlugTaken')}</span>
                  )}
                  {!errors.catalogSlug && !isCheckingCatalogSlug && catalogSlugStatus === 'invalid' && (
                    <span className={styles.errorText}>{t('shops.catalogSlugInvalid')}</span>
                  )}
                  <span className={styles.formHint}>{t('shops.catalogSlugHint')}</span>
                </div>

                {/* Live URL preview + link when slug is set */}
                {formData.catalogSlug && (
                  <div style={{
                    padding: '10px 14px',
                    background: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    flexWrap: 'wrap',
                  }}>
                    <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                      <span style={{ marginRight: '6px' }}>{t('shops.catalogUrlLabel')}</span>
                      <span style={{ fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'monospace' }}>
                        {formData.catalogSlug}.catalog.rettzy.lk
                      </span>
                    </div>
                    {formData.catalogEnabled && (
                      <a
                        href={`https://${formData.catalogSlug}.catalog.rettzy.lk`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '13px',
                          color: 'var(--color-primary)',
                          textDecoration: 'none',
                          flexShrink: 0,
                        }}
                      >
                        <ExternalLink size={14} />
                        {t('shops.catalogPreviewLink')}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* WooCommerce Webhook Integration — provider-only, online and dropshipper shops */}
          {isProvider && formData.type !== 'pos' && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('shops.woocommerceIntegration')}</h2>
                <p>{t('shops.woocommerceIntegrationDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formGroup}>
                  <div className={styles.toggleRow}>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={woocommerceWebhook.enabled}
                        disabled={isWebhookToggling}
                        onChange={(e) => handleToggleWoocommerceWebhook(e.target.checked)}
                      />
                      <span className={styles.slider} />
                    </label>
                    <span>{t('shops.woocommerceWebhookEnabled')}</span>
                  </div>
                  <span className={styles.formHint}>{t('shops.woocommerceWebhookEnabledHint')}</span>
                </div>

                {woocommerceWebhook.enabled && (
                  <>
                    <div style={{
                      padding: '10px 14px',
                      background: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: 'var(--color-text-secondary)',
                      marginBottom: '16px',
                    }}>
                      {t('shops.woocommerceSetupHint')}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="woocommerceOrderCreatedUrl">{t('shops.woocommerceOrderCreatedUrl')}</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className={styles.inputWithIcon} style={{ flex: 1 }}>
                          <Webhook size={18} className={styles.inputIcon} />
                          <input
                            type="text"
                            id="woocommerceOrderCreatedUrl"
                            value={getWoocommerceWebhookUrls().orderCreatedUrl}
                            readOnly
                            style={{ fontFamily: 'monospace', fontSize: '13px' }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCopyToClipboard(getWoocommerceWebhookUrls().orderCreatedUrl)}
                          title={t('shops.woocommerceCopy')}
                          className={styles.bulkEditButton}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="woocommerceOrderStatusUpdatedUrl">{t('shops.woocommerceOrderStatusUpdatedUrl')}</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className={styles.inputWithIcon} style={{ flex: 1 }}>
                          <Webhook size={18} className={styles.inputIcon} />
                          <input
                            type="text"
                            id="woocommerceOrderStatusUpdatedUrl"
                            value={getWoocommerceWebhookUrls().orderStatusUpdatedUrl}
                            readOnly
                            style={{ fontFamily: 'monospace', fontSize: '13px' }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCopyToClipboard(getWoocommerceWebhookUrls().orderStatusUpdatedUrl)}
                          title={t('shops.woocommerceCopy')}
                          className={styles.bulkEditButton}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="woocommerceWebhookSecret">{t('shops.woocommerceWebhookSecret')}</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className={styles.inputWithIcon} style={{ flex: 1 }}>
                          <Webhook size={18} className={styles.inputIcon} />
                          <input
                            type={showWebhookSecret ? 'text' : 'password'}
                            id="woocommerceWebhookSecret"
                            value={woocommerceWebhook.secret}
                            readOnly
                            style={{ fontFamily: 'monospace', fontSize: '13px' }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowWebhookSecret(prev => !prev)}
                          title={showWebhookSecret ? t('shops.woocommerceHideSecret') : t('shops.woocommerceShowSecret')}
                          className={styles.bulkEditButton}
                        >
                          {showWebhookSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyToClipboard(woocommerceWebhook.secret)}
                          title={t('shops.woocommerceCopy')}
                          className={styles.bulkEditButton}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                      <span className={styles.formHint}>{t('shops.woocommerceWebhookSecretHint')}</span>
                    </div>

                    <div className={styles.formGroup}>
                      <button
                        type="button"
                        onClick={handleRegenerateWebhookSecret}
                        disabled={isRegeneratingSecret}
                        className={styles.freeDeliveryButton}
                      >
                        <RefreshCw size={16} />
                        {t('shops.woocommerceRegenerateSecret')}
                      </button>
                    </div>

                    <span className={styles.formHint}>
                      {woocommerceWebhook.lastReceivedAt
                        ? t('shops.woocommerceLastReceivedAt', { date: formatDateTime(woocommerceWebhook.lastReceivedAt) })
                        : t('shops.woocommerceNeverReceived')}
                    </span>

                    {/* REST API credentials — used only by the manual order sync page */}
                    <div style={{ borderTop: '1px solid var(--color-border)', marginTop: '20px', paddingTop: '20px' }}>
                      <h3 style={{ fontSize: '15px', margin: '0 0 4px' }}>{t('shops.woocommerceApiTitle')}</h3>
                      <p className={styles.formHint} style={{ marginBottom: '16px' }}>{t('shops.woocommerceApiDesc')}</p>

                      <div className={styles.formGroup}>
                        <label htmlFor="woocommerceStoreUrl">{t('shops.woocommerceStoreUrl')}</label>
                        <div className={styles.inputWithIcon}>
                          <Globe size={18} className={styles.inputIcon} />
                          <input
                            type="url"
                            id="woocommerceStoreUrl"
                            value={woocommerceApi.storeUrl}
                            onChange={(e) => setWoocommerceApi(prev => ({ ...prev, storeUrl: e.target.value }))}
                            placeholder={t('shops.woocommerceStoreUrlPlaceholder')}
                            autoComplete="off"
                          />
                        </div>
                      </div>

                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label htmlFor="woocommerceConsumerKey">{t('shops.woocommerceConsumerKey')}</label>
                          <input
                            type="text"
                            id="woocommerceConsumerKey"
                            value={woocommerceApi.consumerKey}
                            onChange={(e) => setWoocommerceApi(prev => ({ ...prev, consumerKey: e.target.value }))}
                            placeholder="ck_..."
                            autoComplete="off"
                            style={{ fontFamily: 'monospace', fontSize: '13px' }}
                          />
                          {woocommerceApi.hasCredentials && woocommerceApi.maskedConsumerKey && (
                            <span className={styles.formHint}>
                              {t('shops.woocommerceKeySaved', { key: woocommerceApi.maskedConsumerKey })}
                            </span>
                          )}
                        </div>

                        <div className={styles.formGroup}>
                          <label htmlFor="woocommerceConsumerSecret">{t('shops.woocommerceConsumerSecret')}</label>
                          <input
                            type="password"
                            id="woocommerceConsumerSecret"
                            value={woocommerceApi.consumerSecret}
                            onChange={(e) => setWoocommerceApi(prev => ({ ...prev, consumerSecret: e.target.value }))}
                            placeholder={woocommerceApi.hasCredentials ? t('shops.woocommerceSecretSaved') : 'cs_...'}
                            autoComplete="new-password"
                            style={{ fontFamily: 'monospace', fontSize: '13px' }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={handleSaveWoocommerceApi}
                          disabled={isSavingWoocommerceApi || !woocommerceApi.storeUrl.trim()}
                          className={styles.bulkEditButton}
                        >
                          <Save size={16} />
                          {t('shops.woocommerceSaveApi')}
                        </button>
                        <button
                          type="button"
                          onClick={handleTestWoocommerceApi}
                          disabled={isTestingWoocommerceApi || !woocommerceApi.hasCredentials}
                          title={!woocommerceApi.hasCredentials ? t('shops.woocommerceSaveCredentialsFirst') : undefined}
                          className={styles.freeDeliveryButton}
                        >
                          <RefreshCw size={16} />
                          {t('shops.woocommerceTestConnection')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Message Center Shift Settings — provider-only, online/dropshipper shops, only when the business has the feature enabled */}
          {isProvider && formData.type !== 'pos' && messageCenterSettings.enabled && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('shops.messageCenterShiftSettings')}</h2>
                <p>{t('shops.messageCenterShiftSettingsDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formGroup}>
                  <div className={styles.toggleRow}>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={formData.messageCenterShiftEligible}
                        onChange={(e) => setFormData(prev => ({ ...prev, messageCenterShiftEligible: e.target.checked }))}
                      />
                      <span className={styles.slider} />
                    </label>
                    <span>{t('shops.messageCenterShiftEligible')}</span>
                  </div>
                  <span className={styles.formHint}>{t('shops.messageCenterShiftEligibleHint')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={handleCancel}
              className={styles.cancelButton}
            >
              <X size={18} />
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading || (!isReseller && (isCheckingPrefix || prefixStatus === 'taken' || isCheckingCatalogSlug)) || !hasChanges()}
            >
              {isLoading ? (
                <div className={styles.loadingSpinner}></div>
              ) : (
                <>
                  <Save size={18} />
                  {t('shops.updateShop')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <Edit3 size={24} />
              <h3>{t('shops.bulkEditDeliveryCharges')}</h3>
              <button onClick={() => setShowBulkEditModal(false)} className={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.dialogBody}>
              <div className={styles.formGroup}>
                <label htmlFor="bulkEditValue">{t('shops.deliveryChargeAmount')}</label>
                <div className={styles.inputWithIcon}>
                  <Banknote size={18} className={styles.inputIcon} />
                  <input
                    type="number"
                    id="bulkEditValue"
                    value={bulkEditValue}
                    onChange={(e) => setBulkEditValue(e.target.value)}
                    placeholder="150.00"
                    min="0"
                    step="0.01"
                    className={styles.input}
                    autoFocus
                  />
                  <span className={styles.currencyLabel}>{user?.business?.currency || null}</span>
                </div>
                <span className={styles.formHint}>{t('shops.bulkEditHint')}</span>
              </div>
            </div>
            <div className={styles.dialogActions}>
              <button
                type="button"
                onClick={() => setShowBulkEditModal(false)}
                className={styles.cancelButton}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleBulkEditApply}
                className={styles.submitButton}
              >
                <Check size={16} />
                {t('shops.applyToAll')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditShopPage;
